import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ActionToastContent } from '@/components/action-toast-content';
import { AuthService } from '@domain/auth/blocs/services/auth-service';
import { AuthStatus, VerificationMethod } from '@/enums';
import { captureException, ErrorReporter } from '@/utils/sentry';
import { createStore, StoreApi, useStore } from 'zustand';
import { Hub } from 'aws-amplify';
import { isOfflineModeAllowed } from '@/utils/offline-mode';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import { useApolloClient } from '@apollo/client';
import { useAutoLoginParams } from '@/hooks/use-auto-login-params';
import { useOfflineAuth } from '@/hooks/use-offline-data';
import { useOfflineModeActiveWithoutDelay } from '@/hooks/use-offline-mode-active';
import { useSendChallengeAnswer } from '@/hooks/use-send-challenge-answer';
import { useShallow } from 'zustand/shallow';
import { useToasts } from '@/contexts/toast-context';

interface CheckAuthStatusOptions {
  reset?: boolean;
}

type HubListenParametersType = Parameters<typeof Hub.listen>;

export interface AuthContextType {
  authenticatedUserUUID: string | null;
  authStatus: AuthStatus | null;
  checkAuthStatus: (options?: CheckAuthStatusOptions) => Promise<void>;
  previousAuthStatus: AuthStatus | null;
  setAuthenticatedUserUUID: (uuid: string | null) => void;
  setAuthStatus: (status: AuthStatus | null) => void;
  setPreviousAuthStatus: (status: AuthStatus | null) => void;
}

export const AuthContext = createContext<StoreApi<AuthContextType> | null>(
  null,
);

interface Props {
  children?: React.ReactNode;
}

// Exponential backoff delay for retrying token refresh: 15s -> 30s -> 60s -> 120s
const TOKEN_REFRESH_BACKOFF_DELAYS = [15000, 30000, 60000, 120000];

export const AuthContextProvider: React.FC<Props> = ({ children }) => {
  const apolloClient = useApolloClient();
  const autoLoginParams = useAutoLoginParams();
  const sendChallengeAnswer = useSendChallengeAnswer();
  const tokenRefreshFailureCount = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showToast } = useToasts();

  const isOfflineModeActive = useOfflineModeActiveWithoutDelay();
  const {
    deleteOfflineAuthToken,
    isOfflineAuthTokenValid,
    refreshOfflineAuthToken,
  } = useOfflineAuth();

  // Create zustand store for auth state
  const [store] = useState(() => {
    return createStore<AuthContextType>((set) => ({
      authenticatedUserUUID: null,
      authStatus: null,
      checkAuthStatus: async (options?: CheckAuthStatusOptions) => {
        const { reset } = options ?? {};
        try {
          if (reset) {
            set({ authStatus: null });
          }

          const userUUID = await SessionService.getEmployeeUUID();
          set({ authenticatedUserUUID: userUUID });

          // Identify Sentry and analytics user
          ErrorReporter.setUser({ id: userUUID });
          Analytics.load();
          Analytics.identify(userUUID);

          // Record that the user has a valid auth, for offline mode to use,
          // as long as offline mode is allowed on their platform
          if (isOfflineModeAllowed) {
            await refreshOfflineAuthToken();
          }

          // Set auth status to Authenticated initially
          set((state) => ({
            authStatus: AuthStatus.Authenticated,
            previousAuthStatus: state.authStatus,
          }));
        } catch (e) {
          // Unless we're in offline mode with a valid offline auth token,
          // wipe their active session to forcibly sign them out
          if (!(isOfflineModeActive && isOfflineAuthTokenValid)) {
            set((state) => ({
              authenticatedUserUUID: null,
              authStatus: AuthStatus.NotAuthenticated,
              previousAuthStatus: state.authStatus,
            }));

            // Clear Sentry and analytics user
            ErrorReporter.setUser(null);
            Analytics.reset();

            // When the auth expires or the user signs out, also wipe their offline auth
            deleteOfflineAuthToken();

            // Clear Apollo cache to prevent potential conflicts
            // resetStore refetches queries and causes errors since there's no longer an
            // authenticated user so setTimeout gives the queries time to unmount first
            setTimeout(() => {
              apolloClient.resetStore();
            }, 50);
          } else {
            // User is offline with valid token
            set((state) => ({
              authStatus: AuthStatus.Offline,
              previousAuthStatus: state.authStatus,
            }));
          }
        }
      },
      previousAuthStatus: null,
      setAuthenticatedUserUUID: (uuid) => set({ authenticatedUserUUID: uuid }),
      setAuthStatus: (status) =>
        set((state) => ({
          authStatus: status,
          previousAuthStatus: state.authStatus,
        })),
      setPreviousAuthStatus: (status) => set({ previousAuthStatus: status }),
    }));
  });

  const checkAuthStatus = useCallback(() => {
    return store.getState().checkAuthStatus();
  }, [store]);

  useEffect(() => {
    // Skip initial checkAuthStatus for auto-login users since they're automatically
    // signed in, which calls checkAuthStatus afterward
    if (!autoLoginParams) {
      // Start timing for first screen load (will be overwritten on signIn if auth fails)
      PerfTiming.start(PerfTimingEvent.FIRST_SCREEN_VISIBLE);
      void checkAuthStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register auth event listeners
  useEffect(() => {
    const authEventListener: HubListenParametersType[1] = ({ payload }) => {
      switch (payload.event) {
        case 'signIn':
          // Reset token refresh failure counter on successful sign in
          tokenRefreshFailureCount.current = 0;
          // Start timing for first screen load after sign in
          PerfTiming.start(PerfTimingEvent.FIRST_SCREEN_VISIBLE);
          void checkAuthStatus();
          break;
        case 'signOut':
        case 'signUp':
          void checkAuthStatus();
          break;
        case 'tokenRefresh':
          tokenRefreshFailureCount.current = 0;
          break;
        case 'tokenRefresh_failure': {
          if (isOfflineModeActive && isOfflineAuthTokenValid) {
            // User is offline and we want to respect their last-seen auth,
            // so don't check auth status, because that will fail and sign them out
            break;
          }

          const failureIndex = tokenRefreshFailureCount.current;
          tokenRefreshFailureCount.current++;

          // All automatic retries exhausted, show toast with manual retry button
          if (failureIndex >= TOKEN_REFRESH_BACKOFF_DELAYS.length) {
            showToast({
              autoCloseTimeout: 0,
              content: (onClose) => (
                <ActionToastContent
                  buttonProps={{
                    children: i18n.t('retry'),
                    onPress: () => {
                      onClose();
                      void store.getState().checkAuthStatus();
                    },
                    trackingData: {
                      name: 'auth.token_refresh_retry_button.press',
                    },
                  }}
                  textContent={i18n.t('toast.invalid_user_session')}
                />
              ),
            });
          } else {
            // Retry with exponential backoff to handle transient failures
            const delayMs = TOKEN_REFRESH_BACKOFF_DELAYS[failureIndex];
            retryTimeoutRef.current = setTimeout(() => {
              void store.getState().checkAuthStatus();
            }, delayMs);
          }
          break;
        }
      }
    };

    Hub.listen('auth', authEventListener);
    return () => {
      Hub.remove('auth', authEventListener);

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [
    checkAuthStatus,
    isOfflineAuthTokenValid,
    isOfflineModeActive,
    showToast,
    store,
  ]);

  useEffect(() => {
    if (!autoLoginParams) {
      return;
    }

    (async () => {
      try {
        // Automatically sign in auto-login users
        const user = await AuthService.signIn(autoLoginParams.username);
        const autoLoginPayload = JSON.stringify({
          business_organization_id: autoLoginParams.businessOrganizationUUID,
          buyer_cookie: autoLoginParams.cookie,
        });
        await sendChallengeAnswer(
          user,
          autoLoginPayload,
          VerificationMethod.Punchout,
        );
      } catch (e) {
        captureException(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth state using selectors for fine-grained reactivity
 * @example
 * const authStatus = useAuth((s) => s.authStatus);
 * const checkAuthStatus = useAuth((s) => s.checkAuthStatus);
 */
export const useAuth = <T,>(selector: (s: AuthContextType) => T): T => {
  const store = useContext(AuthContext);
  if (!store) {
    throw new Error('useAuth requires an AuthContextProvider');
  }

  return useStore(store, useShallow(selector));
};

/**
 * Returns the authenticated user's UUID, enforcing that one is present.
 *
 * @remarks
 * Use only where an authenticated user is a precondition; throws otherwise so
 * the violation surfaces loudly instead of propagating a nullable id.
 *
 * @returns The authenticated user's UUID.
 * @throws {Error} When no user is authenticated.
 */
export const useAuthenticatedUserUUID = (): string => {
  const userUUID = useAuth((s) => s.authenticatedUserUUID);
  if (!userUUID) {
    throw new Error('useAuthenticatedUserUUID requires an authenticated user');
  }

  return userUUID;
};

/**
 * Hook to access entire auth context (use sparingly, prefer useAuth with selector)
 */
export const useAuthContext = () => {
  const store = useContext(AuthContext);
  if (!store) {
    throw new Error('useAuthContext requires an AuthContextProvider');
  }

  return useStore(store);
};
